import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { db } from '@/lib/firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '@/lib/firebase';
import { CheckCircle2, Pencil } from 'lucide-react';
import { formatCurrency, getLocaleForCountry, getCurrencySymbol } from '@/lib/currency';
import {
  isPaidSongRequestForArtistLedger,
  payoutDocMatchesArtistRail,
  payoutSourceOf,
  PLATFORM_COMMISSION,
  MIN_MANUAL_PAYOUT_GBP,
  MIN_MANUAL_PAYOUT_USD,
  applyAdminLedgerDisplay,
} from '@/lib/payoutLedger';

const STATUS_STYLES = {
  pending: 'text-yellow-300',
  approved: 'text-blue-300',
  paid: 'text-green-300',
  rejected: 'text-red-300',
};

const STATUS_HELP_TEXT = {
  pending: 'Awaiting admin review.',
  approved: 'Approved. Manual transfer is usually made on the next working day.',
  paid: 'Paid manually by bank transfer.',
  rejected: 'Not approved. Check admin note for details.',
};

const formatDate = (value, locale = 'en-GB') => {
  if (!value) return 'N/A';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString(locale);
};

const normalizeSortCode = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 6);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
};

/** Latest bank details from a completed (paid) payout — same details admin used for transfer. */
function pickSavedBankDetailsFromPaidPayouts(payoutRequests) {
  for (const p of payoutRequests) {
    if (p.status !== 'paid') continue;
    if (String(p.currency || 'GBP').toUpperCase() === 'USD') continue;
    const name = String(p.bank_account_name || '').trim();
    const sortDigits = String(p.bank_sort_code || '').replace(/\D/g, '');
    const acctDigits = String(p.bank_account_number || '').replace(/\D/g, '');
    if (name && sortDigits.length === 6 && acctDigits.length === 8) {
      return {
        bankAccountName: name,
        bankSortCode: normalizeSortCode(sortDigits),
        bankAccountNumber: acctDigits,
      };
    }
  }
  return null;
}

/** Latest US PayPal/Venmo from a completed payout. */
function pickSavedUsPayoutFromPaidPayouts(payoutRequests) {
  for (const p of payoutRequests) {
    if (p.status !== 'paid') continue;
    if (String(p.currency || '').toUpperCase() !== 'USD') continue;
    const method = String(p.payout_method || '').toLowerCase();
    if (method === 'paypal' && String(p.paypal_email || '').includes('@')) {
      return {payoutMethod: 'paypal', paypalEmail: String(p.paypal_email).trim(), venmoHandle: ''};
    }
    if (method === 'venmo' && String(p.venmo_handle || '').trim()) {
      return {
        payoutMethod: 'venmo',
        venmoHandle: String(p.venmo_handle).trim().replace(/^@/, ''),
        paypalEmail: '',
      };
    }
  }
  return null;
}

function maskAccountNumber(last4) {
  const d = String(last4 || '').replace(/\D/g, '').slice(-4);
  if (d.length === 4) return `••••••${d}`;
  return '••••••••';
}

const PAYOUT_REQUEST_ANCHOR_ID = 'ysr-payout-request';

const ManualPayoutsPanel = ({ compact = false, payoutSource = 'artist' }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const isKaraoke = payoutSource === 'karaoke';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [allPayoutRequests, setAllPayoutRequests] = useState([]);
  const [form, setForm] = useState({
    amount: '',
    bankAccountName: '',
    bankSortCode: '',
    bankAccountNumber: '',
    payoutMethod: 'paypal',
    paypalEmail: '',
    venmoHandle: '',
    payoutNote: '',
  });

  useEffect(() => {
    const loadData = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const profileSnap = await getDoc(doc(db, 'profiles', user.uid));
        setProfile(profileSnap.exists() ? profileSnap.data() : null);

        if (isKaraoke) {
          const karaokeSnap = await getDocs(
            query(collection(db, 'karaoke_requests'), where('karaoke_host_id', '==', user.uid))
          );
          setRequests(karaokeSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } else {
          const requestsSnap = await getDocs(
            query(collection(db, 'requests'), where('artist_id', '==', user.uid))
          );
          setRequests(requestsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }

        const payoutSnap = await getDocs(
          query(collection(db, 'artist_payout_requests'), where('artist_id', '==', user.uid))
        );
        const sortedPayouts = payoutSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            const da = a.requested_at?.toDate ? a.requested_at.toDate().getTime() : new Date(a.requested_at || 0).getTime();
            const dbTime = b.requested_at?.toDate ? b.requested_at.toDate().getTime() : new Date(b.requested_at || 0).getTime();
            return dbTime - da;
          });
        setAllPayoutRequests(sortedPayouts);
      } catch (error) {
        console.error('Error loading manual payouts data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load payout data.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user?.uid, toast, isKaraoke]);

  useEffect(() => {
    if (searchParams.get('focus') !== 'request') return;
    const id = window.requestAnimationFrame(() => {
      document.getElementById(PAYOUT_REQUEST_ANCHOR_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    setSearchParams(next, { replace: true });
    return () => window.cancelAnimationFrame(id);
  }, [searchParams, setSearchParams]);

  const ledgerPayoutRequests = useMemo(
    () =>
      allPayoutRequests.filter((p) =>
        isKaraoke ? payoutSourceOf(p) === 'karaoke' : payoutSourceOf(p) !== 'karaoke'
      ),
    [allPayoutRequests, isKaraoke]
  );

  const paidRequests = useMemo(
    () => requests.filter((req) => isPaidSongRequestForArtistLedger(req, profile || {})),
    [requests, profile]
  );

  const grossEarnings = useMemo(() => {
    return paidRequests.reduce((sum, req) => {
      const totalPaid = Number(req.total_paid);
      if (!Number.isNaN(totalPaid) && totalPaid > 0) return sum + totalPaid;
      return sum + Number(req.amount_paid || 0);
    }, 0);
  }, [paidRequests]);

  const netEarnings = useMemo(() => grossEarnings * (1 - PLATFORM_COMMISSION), [grossEarnings]);

  // Source-of-truth for requestable balance should be paid requests, not profile.balance.
  // This prevents stale/legacy profile balances from inflating payout availability.
  const walletBalance = useMemo(() => netEarnings, [netEarnings]);

  const isUsArtist = String(profile?.country || '').toUpperCase() === 'US';

  const alreadyPaid = useMemo(() => {
    if (isUsArtist || isKaraoke) {
      return ledgerPayoutRequests
        .filter((p) => p.status === 'paid' && payoutDocMatchesArtistRail(p, profile || {}))
        .reduce((s, p) => s + Number(p.amount || 0), 0);
    }
    return Number(profile?.payouts_paid_total || 0);
  }, [isUsArtist, isKaraoke, ledgerPayoutRequests, profile?.payouts_paid_total]);

  const reservedLive = useMemo(() => {
    return ledgerPayoutRequests
      .filter(
        (p) =>
          (p.status === 'pending' || p.status === 'approved') &&
          payoutDocMatchesArtistRail(p, profile || {})
      )
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);
  }, [ledgerPayoutRequests, profile]);

  const ledgerDisplay = useMemo(() => {
    const live = {
      gross: grossEarnings,
      artistShare: walletBalance,
      paidOut: alreadyPaid,
      reserved: reservedLive,
    };
    if (isKaraoke) {
      return {
        ...live,
        currentBalance: Math.max(0, walletBalance - alreadyPaid - reservedLive),
      };
    }
    return applyAdminLedgerDisplay(live, profile || {});
  }, [grossEarnings, walletBalance, alreadyPaid, reservedLive, isKaraoke, profile]);

  const availableToRequest = ledgerDisplay.currentBalance;
  const payoutMin = isUsArtist ? MIN_MANUAL_PAYOUT_USD : MIN_MANUAL_PAYOUT_GBP;
  const canRequestPayout = availableToRequest >= payoutMin;

  const payoutCurrency = useMemo(() => {
    if (isUsArtist) return 'USD';
    return (profile?.currency || 'GBP').toString().toUpperCase();
  }, [isUsArtist, profile?.currency]);
  const fmt = (v) => formatCurrency(Number(v || 0), payoutCurrency);
  const listDateLocale = getLocaleForCountry(profile?.country || 'GB');

  const savedBankDetails = useMemo(
    () => pickSavedBankDetailsFromPaidPayouts(allPayoutRequests),
    [allPayoutRequests]
  );

  const savedUsPayout = useMemo(
    () => pickSavedUsPayoutFromPaidPayouts(allPayoutRequests),
    [allPayoutRequests]
  );

  /** When we have paid payouts on file, default to compact "saved" view; artist can expand to edit. */
  const [editingBankDetails, setEditingBankDetails] = useState(false);
  const [editingUsPayout, setEditingUsPayout] = useState(false);

  const showBankForm = !isUsArtist && (!savedBankDetails || editingBankDetails);
  const showUsPayoutForm = isUsArtist && (!savedUsPayout || editingUsPayout);

  const onChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const beginEditBankDetails = () => {
    if (savedBankDetails) {
      setForm((prev) => ({
        ...prev,
        bankAccountName: savedBankDetails.bankAccountName,
        bankSortCode: savedBankDetails.bankSortCode,
        bankAccountNumber: savedBankDetails.bankAccountNumber,
      }));
    }
    setEditingBankDetails(true);
  };

  const cancelEditBankDetails = () => {
    if (savedBankDetails) {
      setForm((prev) => ({
        ...prev,
        bankAccountName: savedBankDetails.bankAccountName,
        bankSortCode: savedBankDetails.bankSortCode,
        bankAccountNumber: savedBankDetails.bankAccountNumber,
      }));
    }
    setEditingBankDetails(false);
  };

  const beginEditUsPayout = () => {
    if (savedUsPayout) {
      setForm((prev) => ({
        ...prev,
        payoutMethod: savedUsPayout.payoutMethod,
        paypalEmail: savedUsPayout.paypalEmail || '',
        venmoHandle: savedUsPayout.venmoHandle || '',
      }));
    }
    setEditingUsPayout(true);
  };

  const cancelEditUsPayout = () => {
    if (savedUsPayout) {
      setForm((prev) => ({
        ...prev,
        payoutMethod: savedUsPayout.payoutMethod,
        paypalEmail: savedUsPayout.paypalEmail || '',
        venmoHandle: savedUsPayout.venmoHandle || '',
      }));
    }
    setEditingUsPayout(false);
  };

  const submitPayoutRequest = async () => {
    if (!canRequestPayout) {
      toast({
        title: 'Minimum balance not reached',
        description: `You need at least ${fmt(payoutMin)} available to request a payout. You currently have ${fmt(availableToRequest)} available.`,
        variant: 'destructive',
      });
      return;
    }

    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast({ title: 'Invalid amount', description: 'Enter a valid amount.', variant: 'destructive' });
      return;
    }
    if (amount > availableToRequest) {
      toast({
        title: 'Amount too high',
        description: `Maximum available is ${fmt(availableToRequest)}.`,
        variant: 'destructive',
      });
      return;
    }

    let payload;

    if (isUsArtist) {
      const method = String(form.payoutMethod || 'paypal').toLowerCase();
      let paypalEmail = '';
      let venmoHandle = '';

      if (savedUsPayout && !editingUsPayout) {
        paypalEmail = savedUsPayout.paypalEmail || '';
        venmoHandle = savedUsPayout.venmoHandle || '';
        if (savedUsPayout.payoutMethod === 'paypal') {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paypalEmail)) {
            toast({
              title: 'PayPal email missing',
              description: 'Edit your details and add a valid PayPal email.',
              variant: 'destructive',
            });
            return;
          }
          payload = {
            amount,
            payoutNote: form.payoutNote || '',
            payoutMethod: 'paypal',
            paypalEmail,
            venmoHandle: '',
          };
        } else {
          const h = String(venmoHandle).replace(/^@/, '');
          if (!/^[a-zA-Z0-9_-]{2,50}$/.test(h)) {
            toast({
              title: 'Venmo username missing',
              description: 'Edit your details and add your Venmo username.',
              variant: 'destructive',
            });
            return;
          }
          payload = {
            amount,
            payoutNote: form.payoutNote || '',
            payoutMethod: 'venmo',
            paypalEmail: '',
            venmoHandle: h,
          };
        }
      } else {
        if (method === 'paypal') {
          paypalEmail = String(form.paypalEmail || '').trim();
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(paypalEmail)) {
            toast({
              title: 'Invalid PayPal email',
              description: 'Enter the email linked to your PayPal account.',
              variant: 'destructive',
            });
            return;
          }
          payload = {
            amount,
            payoutNote: form.payoutNote || '',
            payoutMethod: 'paypal',
            paypalEmail,
            venmoHandle: '',
          };
        } else {
          venmoHandle = String(form.venmoHandle || '').trim().replace(/^@/, '');
          if (!/^[a-zA-Z0-9_-]{2,50}$/.test(venmoHandle)) {
            toast({
              title: 'Invalid Venmo username',
              description: 'Use your Venmo username (letters, numbers, _ or -).',
              variant: 'destructive',
            });
            return;
          }
          payload = {
            amount,
            payoutNote: form.payoutNote || '',
            payoutMethod: 'venmo',
            paypalEmail: '',
            venmoHandle,
          };
        }
      }
    } else {
      let bankAccountName;
      let bankSortCodeOut;
      let bankAccountNumberDigits;

      if (savedBankDetails && !editingBankDetails) {
        bankAccountName = savedBankDetails.bankAccountName;
        bankSortCodeOut = normalizeSortCode(savedBankDetails.bankSortCode);
        bankAccountNumberDigits = savedBankDetails.bankAccountNumber;
      } else {
        const sortCodeDigits = form.bankSortCode.replace(/\D/g, '');
        const accountNumberDigits = form.bankAccountNumber.replace(/\D/g, '');
        if (!form.bankAccountName.trim() || sortCodeDigits.length !== 6 || accountNumberDigits.length !== 8) {
          toast({
            title: 'Bank details incomplete',
            description: 'Use UK format: 6-digit sort code and 8-digit account number.',
            variant: 'destructive',
          });
          return;
        }
        bankAccountName = form.bankAccountName.trim();
        bankSortCodeOut = normalizeSortCode(form.bankSortCode);
        bankAccountNumberDigits = accountNumberDigits;
      }
      payload = {
        amount,
        bankAccountName,
        bankSortCode: bankSortCodeOut,
        bankAccountNumber: bankAccountNumberDigits,
        payoutNote: form.payoutNote || '',
      };
    }

    setSubmitting(true);
    try {
      const fn = httpsCallable(getFunctions(auth.app, 'us-central1'), 'createArtistPayoutRequest');
      const result = await fn(
        isKaraoke ? { ...payload, payoutSource: 'karaoke' } : payload
      );

      if (!result?.data?.success) {
        throw new Error(result?.data?.message || 'Failed to submit payout request');
      }

      toast({
        title: 'Payout request submitted',
        description: `Request for ${fmt(amount)} is now pending admin review.`,
      });

      setForm((prev) => ({
        ...prev,
        amount: '',
        bankAccountName: savedBankDetails?.bankAccountName || prev.bankAccountName,
        bankSortCode: savedBankDetails?.bankSortCode || prev.bankSortCode,
        bankAccountNumber: savedBankDetails?.bankAccountNumber || prev.bankAccountNumber,
        payoutMethod: savedUsPayout?.payoutMethod || prev.payoutMethod || 'paypal',
        paypalEmail: savedUsPayout?.paypalEmail || prev.paypalEmail,
        venmoHandle: savedUsPayout?.venmoHandle || prev.venmoHandle,
        payoutNote: '',
      }));
      if (savedBankDetails) setEditingBankDetails(false);
      if (savedUsPayout) setEditingUsPayout(false);

      const payoutSnap = await getDocs(
        query(collection(db, 'artist_payout_requests'), where('artist_id', '==', user.uid))
      );
      const sortedPayouts = payoutSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const da = a.requested_at?.toDate ? a.requested_at.toDate().getTime() : new Date(a.requested_at || 0).getTime();
          const dbTime = b.requested_at?.toDate ? b.requested_at.toDate().getTime() : new Date(b.requested_at || 0).getTime();
          return dbTime - da;
        });
      setAllPayoutRequests(sortedPayouts);

      const profileSnap = await getDoc(doc(db, 'profiles', user.uid));
      setProfile(profileSnap.exists() ? profileSnap.data() : null);
    } catch (error) {
      console.error('Error submitting payout request:', error);
      toast({
        title: 'Request failed',
        description: error.message || 'Could not submit payout request.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className={`grid ${compact ? 'grid-cols-2' : 'grid-cols-4'} gap-4`}>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <p className="text-gray-400 text-xs">{isKaraoke ? 'Paid requests (total)' : 'Gross Earnings'}</p>
            <p className="text-white text-xl font-semibold">{fmt(ledgerDisplay.gross)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <p className="text-gray-400 text-xs">{isKaraoke ? 'Your share (70%)' : 'Artist Share (70%)'}</p>
            <p className="text-white text-xl font-semibold">{fmt(ledgerDisplay.artistShare)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <p className="text-gray-400 text-xs">Already Paid</p>
            <p className="text-white text-xl font-semibold">{fmt(ledgerDisplay.paidOut)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <p className="text-gray-400 text-xs">Available to Request</p>
            <p className="text-green-300 text-xl font-semibold">{fmt(availableToRequest)}</p>
          </CardContent>
        </Card>
      </div>

      <Card id={PAYOUT_REQUEST_ANCHOR_ID} className="bg-gray-800/50 border-gray-700 scroll-mt-4">
        <CardHeader>
          <CardTitle className="text-white">
            {isUsArtist
              ? isKaraoke
                ? 'Request a payout (US — PayPal or Venmo)'
                : 'Request Payout (US — PayPal or Venmo)'
              : isKaraoke
                ? 'Request a payout (UK bank transfer)'
                : 'Request Payout (UK Bank Transfer)'}
          </CardTitle>
          <CardDescription>
            {isUsArtist ? (
              <>
                {isKaraoke ? (
                  <>
                    Payouts go out in <strong className="text-gray-200">USD</strong> to your{' '}
                    <strong className="text-gray-200">PayPal</strong> or <strong className="text-gray-200">Venmo</strong>.
                    You’ll need at least {fmt(MIN_MANUAL_PAYOUT_USD)} ready to withdraw. After we approve your
                    request, we usually send it on the next working day.
                  </>
                ) : (
                  <>
                    US artists: payouts are sent in <strong className="text-gray-200">USD</strong> to your{' '}
                    <strong className="text-gray-200">PayPal</strong> or <strong className="text-gray-200">Venmo</strong>.
                    You need at least {fmt(MIN_MANUAL_PAYOUT_USD)} available. Processing is usually the next working
                    day after approval.
                  </>
                )}
              </>
            ) : isKaraoke ? (
              <>
                When you’re ready, ask for what you’d like sent. UK payouts use sort code and account number. You’ll
                need at least {fmt(MIN_MANUAL_PAYOUT_GBP)} available. Nothing sends automatically — we process approved
                requests on working days.
              </>
            ) : (
              <>
                Request a manual payout when you have at least {fmt(MIN_MANUAL_PAYOUT_GBP)} available. Approved
                payouts are usually sent on the next working day.
              </>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {availableToRequest > 0 && !canRequestPayout && (
            <div className="rounded-lg border border-amber-700/50 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
              Payout requests open when <strong>available to request</strong> reaches {fmt(payoutMin)}. You
              currently have {fmt(availableToRequest)} available.
            </div>
          )}

          {isUsArtist && savedUsPayout && !editingUsPayout && (
            <div className="rounded-lg border border-green-700/50 bg-green-950/30 px-4 py-3 space-y-2">
              <div className="flex items-center gap-2 text-green-300 text-sm font-medium">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                Payout destination saved
              </div>
              <p className="text-xs text-gray-400">
                We’ll use the same PayPal or Venmo from your last completed US payout unless you edit below.
              </p>
              <dl className="text-sm space-y-1 text-gray-200 mt-2">
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-gray-500">Method</dt>
                  <dd className="capitalize">{savedUsPayout.payoutMethod}</dd>
                </div>
                {savedUsPayout.payoutMethod === 'paypal' ? (
                  <div className="flex flex-wrap gap-x-2">
                    <dt className="text-gray-500">PayPal email</dt>
                    <dd className="break-all">{savedUsPayout.paypalEmail}</dd>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-x-2">
                    <dt className="text-gray-500">Venmo</dt>
                    <dd>@{savedUsPayout.venmoHandle}</dd>
                  </div>
                )}
              </dl>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 border-gray-600 text-gray-200 hover:bg-gray-800"
                onClick={beginEditUsPayout}
              >
                <Pencil className="w-3.5 h-3.5 mr-2" />
                Edit PayPal / Venmo
              </Button>
            </div>
          )}

          {!isUsArtist && savedBankDetails && !editingBankDetails && (
            <div className="rounded-lg border border-green-700/50 bg-green-950/30 px-4 py-3 space-y-2">
              <div className="flex items-center gap-2 text-green-300 text-sm font-medium">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                Bank details saved
              </div>
              <p className="text-xs text-gray-400">
                We’ll use the same UK account from your last completed payout. You only need to enter
                amount (and optional note) unless you want to pay a different account.
              </p>
              <dl className="text-sm space-y-1 text-gray-200 mt-2">
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-gray-500">Account name</dt>
                  <dd>{savedBankDetails.bankAccountName}</dd>
                </div>
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-gray-500">Sort code</dt>
                  <dd className="font-mono">{savedBankDetails.bankSortCode}</dd>
                </div>
                <div className="flex flex-wrap gap-x-2">
                  <dt className="text-gray-500">Account number</dt>
                  <dd className="font-mono">{maskAccountNumber(savedBankDetails.bankAccountNumber)}</dd>
                </div>
              </dl>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2 border-gray-600 text-gray-200 hover:bg-gray-800"
                onClick={beginEditBankDetails}
              >
                <Pencil className="w-3.5 h-3.5 mr-2" />
                Edit bank details
              </Button>
            </div>
          )}

          <div className={`grid ${compact ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
            <div>
              <Label className="text-gray-300">Amount ({getCurrencySymbol(payoutCurrency)})</Label>
              <Input
                value={form.amount}
                onChange={(e) => onChange('amount', e.target.value)}
                type="number"
                min="0"
                step="0.01"
                className="bg-gray-900 border-gray-700 text-white"
              />
            </div>
            {!isUsArtist && showBankForm && (
              <>
                <div>
                  <Label className="text-gray-300">Account Name</Label>
                  <Input
                    value={form.bankAccountName}
                    onChange={(e) => onChange('bankAccountName', e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Sort Code (UK)</Label>
                  <Input
                    value={form.bankSortCode}
                    onChange={(e) => onChange('bankSortCode', normalizeSortCode(e.target.value))}
                    placeholder="12-34-56"
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Account Number (8 digits)</Label>
                  <Input
                    value={form.bankAccountNumber}
                    onChange={(e) => onChange('bankAccountNumber', e.target.value.replace(/\D/g, '').slice(0, 8))}
                    className="bg-gray-900 border-gray-700 text-white"
                  />
                </div>
              </>
            )}

            {isUsArtist && showUsPayoutForm && (
              <>
                <div className="md:col-span-2 space-y-2">
                  <Label className="text-gray-300">Receive payout via</Label>
                  <div className="flex flex-wrap gap-3">
                    <label className="flex items-center gap-2 text-gray-200 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="payoutMethod"
                        checked={form.payoutMethod === 'paypal'}
                        onChange={() => onChange('payoutMethod', 'paypal')}
                        className="accent-purple-500"
                      />
                      PayPal
                    </label>
                    <label className="flex items-center gap-2 text-gray-200 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="payoutMethod"
                        checked={form.payoutMethod === 'venmo'}
                        onChange={() => onChange('payoutMethod', 'venmo')}
                        className="accent-purple-500"
                      />
                      Venmo
                    </label>
                  </div>
                </div>
                {form.payoutMethod === 'paypal' ? (
                  <div className="md:col-span-2">
                    <Label className="text-gray-300">PayPal email</Label>
                    <Input
                      type="email"
                      value={form.paypalEmail}
                      onChange={(e) => onChange('paypalEmail', e.target.value)}
                      placeholder="you@example.com"
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                ) : (
                  <div className="md:col-span-2">
                    <Label className="text-gray-300">Venmo username</Label>
                    <Input
                      value={form.venmoHandle}
                      onChange={(e) => onChange('venmoHandle', e.target.value.replace(/^@/, ''))}
                      placeholder="username (without @)"
                      className="bg-gray-900 border-gray-700 text-white"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          {savedUsPayout && editingUsPayout && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white -mt-1"
              onClick={cancelEditUsPayout}
            >
              Use saved PayPal / Venmo instead
            </Button>
          )}

          {!isUsArtist && savedBankDetails && editingBankDetails && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white -mt-1"
              onClick={cancelEditBankDetails}
            >
              Use saved bank details instead
            </Button>
          )}
          <div>
            <Label className="text-gray-300">Note (optional)</Label>
            <Input
              value={form.payoutNote}
              onChange={(e) => onChange('payoutNote', e.target.value)}
              className="bg-gray-900 border-gray-700 text-white"
            />
          </div>
          <Button
            onClick={submitPayoutRequest}
            disabled={submitting || !canRequestPayout}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {submitting ? 'Submitting...' : 'Submit Payout Request'}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Payout Request History</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {ledgerPayoutRequests.length === 0 ? (
            <p className="text-gray-400 text-sm">No payout requests yet.</p>
          ) : (
            ledgerPayoutRequests.map((item) => (
              <div key={item.id} className="p-3 rounded-lg bg-gray-900/60 border border-gray-700">
                <div className="flex items-center justify-between">
                  <p className="text-white font-semibold">{fmt(item.amount)}</p>
                  <p className={`text-sm uppercase ${STATUS_STYLES[item.status] || 'text-gray-300'}`}>
                    {item.status || 'pending'}
                  </p>
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {STATUS_HELP_TEXT[item.status] || STATUS_HELP_TEXT.pending}
                </p>
                <p className="text-xs text-gray-400 mt-1">Requested: {formatDate(item.requested_at, listDateLocale)}</p>
                {item.processed_at && (
                  <p className="text-xs text-gray-400">Processed: {formatDate(item.processed_at, listDateLocale)}</p>
                )}
                {item.admin_note && <p className="text-xs text-gray-300 mt-1">Admin note: {item.admin_note}</p>}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ManualPayoutsPanel;
